import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { blogService } from '../../services/blogService';
import classes from './styles/blogDetail.module.css';
import BlogImage from '../../components/common/BlogImage';

// ── Structured data ──────────────────────────────────────────
const buildArticleSchema = (blog, slug) => ({
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  "headline": blog.title,
  "description": blog.meta_description || stripHtml(blog.content).substring(0, 160),
  "url": `https://www.fetchjobs.co/blog/${slug}`,
  "datePublished": blog.createdAt,
  "dateModified": blog.updatedAt || blog.createdAt,
  "author": {
    "@type": "Person",
    "name": blog.author && blog.author !== 'string' ? blog.author : 'FetchJobs Team'
  },
  "publisher": {
    "@type": "Organization",
    "name": "FetchJobs",
    "logo": {
      "@type": "ImageObject",
      "url": "https://www.fetchjobs.co/logo.png"
    }
  },
  ...(blog.featured_image_url && {
    "image": {
      "@type": "ImageObject",
      "url": blog.featured_image_url
    }
  }),
  ...(blog.tags?.length > 0 && blog.tags[0] !== 'string' && {
    "keywords": blog.tags.join(", ")
  }),
  ...(blog.category && blog.category !== 'string' && {
    "articleSection": blog.category
  }),
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": `https://www.fetchjobs.co/blog/${slug}`
  }
});

const buildBreadcrumbSchema = (blog, slug) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.fetchjobs.co" },
    { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://www.fetchjobs.co/blog" },
    { "@type": "ListItem", "position": 3, "name": blog.title, "item": `https://www.fetchjobs.co/blog/${slug}` }
  ]
});

// ── Helpers ──────────────────────────────────────────────────
const stripHtml = (html) => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString(undefined, {
    year: 'numeric', month: 'long', day: 'numeric'
  });
};

// ── Component ────────────────────────────────────────────────
const BlogDetail = () => {
  const { slug } = useParams();
  const [blog, setBlog]                 = useState(null);
  const [featuredBlogs, setFeaturedBlogs] = useState([]);
  const [categories, setCategories]     = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [copySuccess, setCopySuccess]   = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);

    const fetchAllData = async () => {
      setLoading(true);
      setError(null);

      const blogResult = await blogService.getBlogBySlug(slug);

      if (!blogResult.success || !blogResult.blog) {
        setError(blogResult.error || 'Blog not found');
        setLoading(false);
        return;
      }

      setBlog(blogResult.blog);

      const [featuredResult, categoriesResult] = await Promise.all([
        blogService.getFeaturedBlogs(blogResult.blog._id, 3),
        blogService.getAllCategories()
      ]);

      if (featuredResult.success)   setFeaturedBlogs(featuredResult.blogs);
      if (categoriesResult.success) setCategories(categoriesResult.categories);

      setLoading(false);
    };

    fetchAllData();
  }, [slug]);

  const handleShare = async (platform) => {
    const url   = window.location.href;
    const title = blog.title;
    const shareUrls = {
      twitter:  `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`
    };
    if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'noopener,noreferrer');
      return;
    }
    if (platform === 'copy') {
      try {
        await navigator.clipboard.writeText(url);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  // ── Derived meta values ──────────────────────────────────
  const canonicalUrl  = `https://www.fetchjobs.co/blog/${slug}`;
  const metaTitle     = blog ? `${blog.meta_title || blog.title} | FetchJobs Blog` : 'Blog | FetchJobs';
  const metaDesc      = blog
    ? (blog.meta_description || stripHtml(blog.content).substring(0, 160))
    : 'Read the latest articles on hiring, career growth, and remote work at FetchJobs.';
  const ogImage       = blog?.featured_image_url || 'https://www.fetchjobs.co/logo.png';

  if (loading) {
    return (
      <div className={classes.container}>
        <Helmet>
          <title>Loading… | FetchJobs Blog</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className={classes.loadingContainer}>
          <div className={classes.loader}></div>
          <p>Loading article...</p>
        </div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className={classes.container}>
        <Helmet>
          <title>Article Not Found | FetchJobs Blog</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div className={classes.errorContainer}>
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="12" cy="12" r="10" stroke="#004AA6" strokeWidth="2"/>
            <line x1="12" y1="8" x2="12" y2="12" stroke="#004AA6" strokeWidth="2" strokeLinecap="round"/>
            <circle cx="12" cy="16" r="1" fill="#004AA6"/>
          </svg>
          <h3>Article Not Found</h3>
          <p>{error || "The article you're looking for doesn't exist."}</p>
          <Link to="/blog" className={classes.backBtn}>Back to Blog</Link>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <Helmet>
        {/* Core */}
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        {blog.tags?.length > 0 && blog.tags[0] !== 'string' && (
          <meta name="keywords" content={blog.tags.join(', ')} />
        )}

        {/* Canonical */}
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph — article type for blog posts */}
        <meta property="og:type"                content="article" />
        <meta property="og:title"               content={metaTitle} />
        <meta property="og:description"         content={metaDesc} />
        <meta property="og:url"                 content={canonicalUrl} />
        <meta property="og:site_name"           content="FetchJobs" />
        <meta property="og:image"               content={ogImage} />
        <meta property="article:published_time" content={blog.createdAt} />
        <meta property="article:modified_time"  content={blog.updatedAt || blog.createdAt} />
        {blog.author && blog.author !== 'string' && (
          <meta property="article:author" content={blog.author} />
        )}
        {blog.category && blog.category !== 'string' && (
          <meta property="article:section" content={blog.category} />
        )}
        {blog.tags?.filter(t => t !== 'string').map((tag, i) => (
          <meta key={i} property="article:tag" content={tag} />
        ))}

        {/* Twitter Card */}
        <meta name="twitter:card"        content="summary_large_image" />
        <meta name="twitter:title"       content={metaTitle} />
        <meta name="twitter:description" content={metaDesc} />
        <meta name="twitter:image"       content={ogImage} />

        {/* Robots */}
        <meta name="robots" content="index, follow" />

        {/* BlogPosting schema — enables Google article rich results */}
        <script type="application/ld+json">
          {JSON.stringify(buildArticleSchema(blog, slug))}
        </script>

        {/* Breadcrumb schema */}
        <script type="application/ld+json">
          {JSON.stringify(buildBreadcrumbSchema(blog, slug))}
        </script>
      </Helmet>

      {/* Breadcrumb — visible */}
      <nav className={classes.breadcrumb} aria-label="Breadcrumb">
        <Link to="/blog">Blog</Link>
        <span aria-hidden="true">/</span>
        <span aria-current="page">{blog.title}</span>
      </nav>

      <div className={classes.contentWrapper}>
        {/* Main Content */}
        <div className={classes.mainContent}>
          <div className={classes.featuredImage}>
            <BlogImage
              imageUrl={blog?.featured_image_url}
              alt={blog.title}
              className={classes.featuredPostImage}
            />
          </div>

          <div className={classes.blogHeader}>
            <h1 className={classes.blogTitle}>{blog.title}</h1>
            <div className={classes.blogMeta}>
              <span className={classes.author}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M20 21V19C20 16.8 18.2 15 16 15H8C5.8 15 4 16.8 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                {blog.author && blog.author !== 'string' ? blog.author : 'Admin'}
              </span>
              <span className={classes.date}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                  <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2"/>
                </svg>
                <time dateTime={blog.createdAt}>{formatDate(blog.createdAt)}</time>
              </span>
              {blog.category && blog.category !== 'string' && (
                <span className={classes.category}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="currentColor" strokeWidth="2"/>
                    <path d="M2 8H22" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  <Link to={`/blog/category/${encodeURIComponent(blog.category)}`}>
                    {blog.category}
                  </Link>
                </span>
              )}
            </div>
          </div>

          <article className={classes.blogContent} dangerouslySetInnerHTML={{ __html: blog.content }} />

          {blog.tags?.length > 0 && blog.tags[0] !== 'string' && (
            <div className={classes.tagsSection}>
              <h3>Tags:</h3>
              <div className={classes.tags}>
                {blog.tags.map((tag, index) => (
                  <span key={index} className={classes.tag}>#{tag}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <aside className={classes.sidebar} aria-label="Blog sidebar">
          {featuredBlogs.length > 0 && (
            <div className={classes.featuredSection}>
              <h3>Featured Posts</h3>
              {featuredBlogs.map((featuredBlog) => (
                <Link
                  key={featuredBlog._id}
                  to={`/blog/${featuredBlog.slug}`}
                  className={classes.featuredPost}
                >
                  <BlogImage
                    imageUrl={featuredBlog?.featured_image_url}
                    alt={featuredBlog.title}
                    className={classes.featuredPostImage}
                  />
                  <div className={classes.featuredPostContent}>
                    <h4>{featuredBlog.title}</h4>
                    <time dateTime={featuredBlog.createdAt}>
                      {formatDate(featuredBlog.createdAt)}
                    </time>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {categories.length > 0 && (
            <div className={classes.categoriesSection}>
              <h3>All Categories</h3>
              <div className={classes.categoriesList}>
                {categories.map((category, index) => (
                  <Link
                    key={index}
                    to={`/blog/category/${encodeURIComponent(category)}`}
                    className={classes.categoryLink}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="#004AA6" strokeWidth="2"/>
                      <path d="M2 8H22" stroke="#004AA6" strokeWidth="2"/>
                    </svg>
                    {category}
                    <span className={classes.arrow} aria-hidden="true">→</span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className={classes.shareSection}>
            <h3>Share this article</h3>
            <div className={classes.shareButtons}>
              <button onClick={() => handleShare('twitter')}  className={classes.shareBtn} aria-label="Share on Twitter">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M23 3C22.1 3.9 21 4.5 20 4.8C19.3 4 18.4 3.5 17.4 3.5C15 3.5 13 5.5 13 7.9C13 8.3 13 8.7 13.1 9.1C9.7 8.9 6.6 7.1 4.5 4.4C4.1 5.1 3.9 5.9 3.9 6.7C3.9 8.3 4.8 9.7 6.1 10.6C5.4 10.6 4.7 10.4 4.1 10.1C4.1 12 5.5 13.6 7.3 13.9C6.9 14 6.5 14.1 6.1 14.1C5.8 14.1 5.5 14.1 5.2 14C5.5 15.6 7 16.8 8.8 16.8C7.4 17.9 5.7 18.5 3.9 18.5C3.5 18.5 3.1 18.5 2.7 18.4C4.5 19.6 6.6 20.3 8.9 20.3C17.4 20.3 22 13.2 22 7C22 6.8 22 6.5 22 6.3C22.9 5.6 23.7 4.8 24 3.9L23 3Z" fill="currentColor"/>
                </svg>
                Twitter
              </button>
              <button onClick={() => handleShare('linkedin')} className={classes.shareBtn} aria-label="Share on LinkedIn">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M4.98 3.5C4.98 4.88 3.87 6 2.5 6C1.13 6 0 4.88 0 3.5C0 2.12 1.13 1 2.5 1C3.87 1 4.98 2.12 4.98 3.5ZM5 8H0V23H5V8ZM9 23H14V14C14 11.5 14.5 9 17.5 9C20.5 9 20.5 11.5 20.5 14V23H25.5V13C25.5 9 24.5 6 20.5 6C18.5 6 17 7 16 8.5H15.5V8H9V23Z" fill="currentColor"/>
                </svg>
                LinkedIn
              </button>
              <button onClick={() => handleShare('facebook')} className={classes.shareBtn} aria-label="Share on Facebook">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M24 12C24 5.4 18.6 0 12 0C5.4 0 0 5.4 0 12C0 18 4.4 22.9 10.1 23.8V15.5H7.1V12H10.1V9.3C10.1 6.3 11.9 4.6 14.6 4.6C15.9 4.6 17.3 4.9 17.3 4.9V7.8H15.8C14.3 7.8 13.8 8.7 13.8 9.6V12H17.1L16.4 15.5H13.7V23.9C19.6 23.1 24 18.1 24 12Z" fill="currentColor"/>
                </svg>
                Facebook
              </button>
              <button
                onClick={() => handleShare('copy')}
                className={`${classes.shareBtn} ${copySuccess ? classes.copied : ''}`}
                aria-label="Copy link"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M16 1H4C2.9 1 2 1.9 2 3V17H4V3H16V1ZM19 5H8C6.9 5 6 5.9 6 7V21C6 22.1 6.9 23 8 23H19C20.1 23 21 22.1 21 21V7C21 5.9 20.1 5 19 5ZM19 21H8V7H19V21Z" fill="currentColor"/>
                </svg>
                {copySuccess ? 'Copied!' : 'Copy Link'}
              </button>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default BlogDetail;